import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import api from '../utils/api';

const EMOJIS = ['🥤','🍺','🥛','🛒','🌽','🧴','🍞','🫙','🌾','🧀','🍶','🟤','🟠','🟢','🟡','☕','🍫','💊','🐾','🧹','🍎','🫐','🥩','🥚','🥬','🐟','🧁','🍗','💧','🫖'];
const UNITS  = ['unid','kg','g','L','ml','doc','pack'];

export default function ProductForm({ product, prefillBarcode, onClose }) {
  const { categories, createProduct, updateProduct } = useApp();
  const fileRef = useRef();
  const isEdit  = !!product?.id;

  const [f, setF] = useState({
    name:              product?.name              || '',
    barcode:           product?.barcode           || prefillBarcode || '',
    category_id:       product?.category_id       || categories[0]?.id || 'otro',
    price:             product?.price             ?? '',
    cost:              product?.cost              ?? '',
    stock:             product?.stock             ?? '',
    min_stock:         product?.min_stock         ?? 5,
    unit:              product?.unit              || 'unid',
    by_weight:         product?.by_weight         ? true : false,
    description:       product?.description       || '',
    image_url:         product?.image_url         || '',
    expiry_date:       product?.expiry_date        || '',
    expiry_alert_days: product?.expiry_alert_days  ?? 7,
  });
  const [imgPreview, setImgPreview] = useState(product?.image_url || '');
  const [curEmoji,   setCurEmoji]   = useState(product?.emoji || (product?.image_url ? '' : '📦'));
  const [uploading,  setUploading]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [errs,       setErrs]       = useState({});

  const set = k => e => { setF(p => ({ ...p, [k]: e.target.value })); setErrs(p => ({ ...p, [k]: null })); };
  const setCheck = k => e => setF(p => ({ ...p, [k]: e.target.checked }));

  const handleFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (file.size > 5e6) { alert('Imagen muy grande (max 5MB)'); return; }
    const reader = new FileReader();
    reader.onload = ev => { setImgPreview(ev.target.result); setCurEmoji(''); };
    reader.readAsDataURL(file);
    setUploading(true);
    try {
      const { image_url } = await api.uploadImage(file);
      setF(p => ({ ...p, image_url }));
    } catch {
      const r2 = new FileReader();
      r2.onload = ev => setF(p => ({ ...p, image_url: ev.target.result }));
      r2.readAsDataURL(file);
    } finally { setUploading(false); }
  };

  const validate = () => {
    const e = {};
    if (!f.name.trim())                               e.name  = 'Requerido';
    if (!f.price || isNaN(f.price) || +f.price <= 0) e.price = 'Precio inválido';
    if (f.stock === '' || isNaN(f.stock) || +f.stock < 0) e.stock = 'Stock inválido';
    setErrs(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    const data = {
      ...f,
      price:             +f.price,
      cost:              +(f.cost || 0),
      stock:             +f.stock,
      min_stock:         +(f.min_stock || 5),
      emoji:             curEmoji,
      by_weight:         f.by_weight ? 1 : 0,
      expiry_alert_days: +(f.expiry_alert_days || 7),
    };
    const ok = isEdit ? await updateProduct(product.id, data) : await createProduct(data);
    setSaving(false);
    if (ok) onClose();
  };

  const margin = f.price && f.cost && +f.cost > 0
    ? (((+f.price - +f.cost) / +f.cost) * 100).toFixed(1) : null;

  return (
    <div style={S.overlay}>
      <div style={S.sheet}>
        <div style={S.hdr}>
          <h3 style={{ fontSize:16, fontWeight:700 }}>{isEdit ? 'Editar producto' : 'Nuevo producto'}</h3>
          <button style={S.xbtn} onClick={onClose}>✕</button>
        </div>

        <div style={S.body}>
          {/* Image / emoji picker */}
          <div style={{ ...S.imgArea, ...(uploading ? { opacity:.7 } : {}) }} onClick={() => fileRef.current.click()}>
            {imgPreview
              ? <img src={imgPreview} alt="" style={S.imgPreview}/>
              : <span style={{ fontSize:52 }}>{curEmoji || '📷'}</span>
            }
            <div style={S.imgBadge}>{uploading ? '⏳' : '📷'} Cambiar imagen</div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile}/>

          {/* Emoji row */}
          <div style={S.emojiRow}>
            {EMOJIS.map(e => (
              <button key={e} style={{ ...S.ebtn, ...(e===curEmoji && !imgPreview ? S.ebtnOn : {}) }}
                onClick={() => { setCurEmoji(e); setImgPreview(''); setF(p => ({ ...p, image_url:'' })); }}>
                {e}
              </button>
            ))}
          </div>

          <Fld label="Nombre *" err={errs.name}>
            <input style={{ ...S.inp, ...(errs.name ? S.inpErr : {}) }} value={f.name} onChange={set('name')} placeholder="Nombre del producto"/>
          </Fld>

          <div style={S.g2}>
            <Fld label="Código de barras">
              <input style={{ ...S.inp, fontFamily:'monospace', fontSize:11 }} value={f.barcode} onChange={set('barcode')} placeholder="Escanea o escribe"/>
            </Fld>
            <Fld label="Categoría">
              <select style={S.sel} value={f.category_id} onChange={set('category_id')}>
                {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
              </select>
            </Fld>
          </div>

          <div style={S.g3}>
            <Fld label={f.by_weight ? 'Precio por kg c/IGV (S/) *' : 'Precio c/IGV (S/) *'} err={errs.price}>
              <input style={{ ...S.inp, ...(errs.price ? S.inpErr : {}) }} type="number" step="0.10" value={f.price} onChange={set('price')} placeholder="0.00"/>
            </Fld>
            <Fld label="Costo (S/)">
              <input style={S.inp} type="number" step="0.10" value={f.cost} onChange={set('cost')} placeholder="0.00"/>
            </Fld>
            <Fld label="Unidad">
              <select style={S.sel} value={f.unit} onChange={set('unit')}>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </select>
            </Fld>
          </div>

          {/* Toggle venta por peso */}
          <div style={S.weightToggle} onClick={() => setF(p => ({ ...p, by_weight: !p.by_weight }))}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--t0)', marginBottom: 2 }}>
                ⚖️ Venta por peso
              </p>
              <p style={{ fontSize: 11, color: 'var(--t1)' }}>
                {f.by_weight
                  ? 'Al agregar al carrito se pedirá el peso en kg'
                  : 'Activar si el precio depende del peso (carnes, quesos, etc.)'}
              </p>
            </div>
            <div style={{ ...S.toggleTrack, ...(f.by_weight ? S.toggleOn : {}) }}>
              <div style={{ ...S.toggleThumb, ...(f.by_weight ? S.thumbOn : {}) }}/>
            </div>
          </div>

          {f.by_weight && (
            <div style={S.weightInfo}>
              💡 El precio ingresado es <strong>por kg</strong>. Al vender, el cajero ingresa el peso real y el sistema calcula el total automáticamente.
            </div>
          )}

          <div style={S.g2}>
            <Fld label="Stock actual *" err={errs.stock}>
              <input style={{ ...S.inp, ...(errs.stock ? S.inpErr : {}) }} type="number" value={f.stock} onChange={set('stock')} placeholder="0"/>
            </Fld>
            <Fld label="Stock mínimo">
              <input style={S.inp} type="number" value={f.min_stock} onChange={set('min_stock')} placeholder="5"/>
            </Fld>
          </div>

          {margin && (
            <div style={S.marginBadge}>
              <span>Margen: {margin}%</span>
              <span>Ganancia: S/ {(+f.price - +f.cost).toFixed(2)} c/u</span>
            </div>
          )}

          <Fld label="Descripción">
            <textarea style={S.textarea} rows={2} value={f.description} onChange={set('description')} placeholder="Opcional..."/>
          </Fld>

          <div style={S.g2}>
            <Fld label="Fecha de vencimiento">
              <input style={S.inp} type="date" value={f.expiry_date} onChange={set('expiry_date')}/>
            </Fld>
            <Fld label="Alertar (días antes)">
              <input style={S.inp} type="number" value={f.expiry_alert_days} onChange={set('expiry_alert_days')} placeholder="7" min="1" max="365"/>
            </Fld>
          </div>

          {f.expiry_date && (() => {
            const days = Math.ceil((new Date(f.expiry_date) - new Date()) / 86400000);
            const color = days < 0 ? 'var(--err)' : days <= +f.expiry_alert_days ? 'var(--warn)' : 'var(--ok)';
            const msg   = days < 0 ? `Vencido hace ${Math.abs(days)} días` : days === 0 ? 'Vence hoy' : `Vence en ${days} días`;
            return (
              <div style={{ background:'rgba(0,0,0,.2)', border:`0.5px solid ${color}`, borderRadius:8, padding:'7px 11px', fontSize:12, color, marginBottom:10 }}>
                📅 {msg}
              </div>
            );
          })()}
        </div>

        <div style={S.footer}>
          <button style={S.cancelBtn} onClick={onClose}>Cancelar</button>
          <button style={S.saveBtn} onClick={handleSubmit} disabled={saving || uploading}>
            {saving ? 'Guardando...' : isEdit ? '✓ Actualizar' : '+ Agregar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Fld = ({ label, err, children }) => (
  <div style={{ marginBottom:10 }}>
    <label style={{ display:'block', fontSize:10, color:'var(--t1)', fontWeight:600, textTransform:'uppercase', letterSpacing:.4, marginBottom:4 }}>{label}</label>
    {children}
    {err && <span style={{ fontSize:11, color:'var(--err)', display:'block', marginTop:2 }}>{err}</span>}
  </div>
);

const S = {
  overlay:     { position:'fixed', inset:0, background:'rgba(0,0,0,.85)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:100, backdropFilter:'blur(4px)' },
  sheet:       { background:'var(--bg1)', width:'100%', maxWidth:600, borderRadius:'20px 20px 0 0', maxHeight:'94vh', display:'flex', flexDirection:'column' },
  hdr:         { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:'0.5px solid var(--bd)', flexShrink:0 },
  xbtn:        { background:'var(--bg2)', color:'var(--t1)', border:'0.5px solid var(--bd)', borderRadius:7, padding:'4px 9px', fontSize:12 },
  body:        { overflowY:'auto', padding:'14px 16px', flex:1 },
  footer:      { display:'flex', gap:10, padding:'10px 16px', borderTop:'0.5px solid var(--bd)', flexShrink:0 },
  imgArea:     { width:'100%', height:120, background:'var(--bg2)', borderRadius:12, border:'1.5px dashed var(--bd2)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', marginBottom:10, position:'relative', overflow:'hidden' },
  imgPreview:  { width:'100%', height:'100%', objectFit:'cover' },
  imgBadge:    { position:'absolute', bottom:6, right:8, background:'rgba(0,0,0,.6)', borderRadius:7, padding:'3px 8px', fontSize:11, color:'#fff' },
  emojiRow:    { display:'flex', gap:5, marginBottom:12, overflowX:'auto', paddingBottom:2, scrollbarWidth:'none' },
  ebtn:        { width:32, height:32, borderRadius:7, background:'var(--bg2)', border:'0.5px solid var(--bd2)', fontSize:17, cursor:'pointer', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' },
  ebtnOn:      { background:'rgba(232,67,90,.18)', border:'1.5px solid var(--acc)' },
  g2:          { display:'grid', gridTemplateColumns:'1fr 1fr', gap:9 },
  g3:          { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:9 },
  inp:         { width:'100%', background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:9, padding:'9px 12px', color:'var(--t0)', fontSize:13 },
  inpErr:      { borderColor:'var(--err)' },
  sel:         { width:'100%', background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:9, padding:'9px 12px', color:'var(--t0)', fontSize:13 },
  textarea:    { width:'100%', background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:9, padding:'9px 12px', color:'var(--t0)', fontSize:13, resize:'none' },
  marginBadge: { background:'rgba(0,201,167,.1)', border:'0.5px solid rgba(0,201,167,.25)', borderRadius:8, padding:'7px 11px', fontSize:11, color:'var(--teal)', marginBottom:10, display:'flex', justifyContent:'space-between' },
  cancelBtn:   { flex:1, background:'var(--bg2)', color:'var(--t1)', border:'0.5px solid var(--bd)', borderRadius:10, padding:12, fontSize:14, fontWeight:500 },
  saveBtn:     { flex:2, background:'var(--acc)', color:'#fff', border:'none', borderRadius:10, padding:12, fontSize:14, fontWeight:700 },
  weightToggle:{ display:'flex', alignItems:'center', gap:12, background:'var(--bg2)', border:'0.5px solid var(--bd2)', borderRadius:10, padding:'11px 13px', marginBottom:10, cursor:'pointer' },
  weightInfo:  { background:'rgba(255,184,0,.08)', border:'0.5px solid rgba(255,184,0,.3)', borderRadius:8, padding:'8px 12px', fontSize:12, color:'#f0b400', marginBottom:10, lineHeight:1.5 },
  toggleTrack: { width:42, height:24, borderRadius:12, background:'var(--bd2)', position:'relative', transition:'background .2s', flexShrink:0 },
  toggleOn:    { background:'var(--acc)' },
  toggleThumb: { position:'absolute', top:3, left:3, width:18, height:18, borderRadius:'50%', background:'#fff', transition:'left .2s', boxShadow:'0 1px 3px rgba(0,0,0,.3)' },
  thumbOn:     { left:21 },
};
